import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const GramsabhaNoticeNoAacharsanhitaWordForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Gramsabha_Notice_26Jan2026_No_Aacharsanhita.pdf' : 'Gramsabha_Notice_26Jan2026_No_Aacharsanhita_EN.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm;
            margin: 0 auto 20px auto;
            box-sizing: border-box;
            position: relative;
            background-color: white;
            overflow: hidden;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 210mm !important;
              height: 297mm !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              page-break-after: always !important;
              break-after: page !important;
              break-inside: avoid !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="flex flex-col items-center">
        {/* PAGE 1 */}
        <div className="bg-white text-black shadow-lg print:shadow-none relative box-border flex flex-col page">
           <div className="text-center mb-2">
              <p className="text-[14px] font-bold">{language === 'marathi' ? 'परिशिष्ट –अ (सभेच्या सुचनेचा नमुना )' : 'Appendix - A (Format of Notice of Meeting)'}</p>
              <p className="text-[12px]">{language === 'marathi' ? '(ग्राम विकास विभाग शासन निर्णय क्र. संग्राम-२०१४ प्र.क्र. ४३/ संग्राम कक्ष दि. २५ जून २०१४ अन्वये )' : '(As per Rural Development Department GR No. Sangram-2014/C.R.43/Sangram Cell Dt. 25 June 2014)'}</p>
           </div>
           
           <div className="text-center mb-6">
              <h1 className="text-2xl font-bold">{language === 'marathi' ? 'ग्रामसभेची सुचना' : 'Gramsabha Notice'}</h1>
           </div>

           <div className="flex justify-between text-[16px] mb-8">
              <div className="flex flex-col space-y-1 w-[300px]">
                 <p className="flex items-end">
                    <span className="mr-1">{language === 'marathi' ? 'ग्रा.पं.कार्यालय' : 'G.P. Office'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </p>
                 <p className="flex items-end">
                    <span className="dashed-line w-20 h-4 mr-1"></span>
                    <span className="mr-1">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </p>
                 <p className="flex items-end">
                    <span className="mr-2">{language === 'marathi' ? '/०१/२०२६' : '/01/2026'}</span>
                 </p>
              </div>
              <div className="flex flex-col space-y-1 w-[200px] text-right">
                 <p className="flex items-end justify-end">
                    <span className="mr-1">{language === 'marathi' ? 'जा.क्र.-------' : 'Outward No.-------'}</span>
                 </p>
                 <p className="flex items-end justify-end mt-2">
                    <span className="mr-1">{language === 'marathi' ? 'ता.------------' : 'Tal.------------'}</span>
                 </p>
                 <p className="flex items-end justify-end mt-2">
                    <span className="mr-2">{language === 'marathi' ? 'दिनांक: -----' : 'Date: -----'}</span>
                 </p>
              </div>
           </div>

           <div className="text-[16px] mb-4 space-y-2">
              <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
              <p className="flex items-end">
                 <span className="mr-2">{language === 'marathi' ? 'श्री./श्रीमती' : 'Mr./Mrs.'}</span>
                 <span className="dashed-line flex-grow h-4"></span>
              </p>
              <p className="flex items-end">
                 <span className="mr-2">{language === 'marathi' ? 'उपसरपंच/सदस्य, सर्व ग्रामस्थ ग्रामपंचायत' : 'Deputy Sarpanch/Member, All Villagers Gram Panchayat'}</span>
                 <span className="dashed-line flex-grow h-4"></span>
              </p>
           </div>

           <div className="mb-4">
              <h2 className="text-[17px] font-bold">
                {language === 'marathi' ? 'विषय : दिनांक २६ /०१/२०२६ रोजी घेण्यात येणा-या ग्रामसभेस उपस्थित राहणेबाबत.' : 'Subject : Regarding presence in Gramsabha to be held on 26/01/2026.'}
              </h2>
           </div>

           <div className="text-[16px] space-y-4">
              <p>{language === 'marathi' ? 'महोदय,' : 'Respected Sir/Madam,'}</p>
              <p className="flex items-end">
                 <span>{language === 'marathi' ? 'दिनांक २६/०१/२०२६ सोमवार रोजी ग्रामपंचायत' : 'On Monday, 26/01/2026, Gram Panchayat'}</span>
                 <span className="dashed-line flex-grow h-4 mx-1"></span>
                 <span>{language === 'marathi' ? 'ची' : "'s"}</span>
              </p>
              <p>{language === 'marathi' ? 'ग्रामसभा आयोजित करण्यात आली आहे.' : 'Gramsabha has been organized.'}</p>
              
              <p className="font-bold flex items-end">
                 <span>{language === 'marathi' ? 'सभेचा दिनांक व वेळ: २६/०१/२०२६ सकाळी/दुपारी/ सायं' : 'Date and time of meeting: 26/01/2026 Morning/Afternoon/Evening'}</span>
                 <span className="dashed-line w-24 h-4 mx-1"></span>
                 <span>{language === 'marathi' ? 'वाजता' : "O'clock"}</span>
              </p>

              <p className="font-bold flex items-end">
                 <span>{language === 'marathi' ? 'सभेचे ठिकाण:' : 'Place of meeting:'}</span>
                 <span className="dashed-line flex-grow h-4 ml-1"></span>
              </p>

              <div className="mt-4">
                 <p className="font-bold mb-3">{language === 'marathi' ? 'सभेची विषयपत्रिका:' : 'Agenda of the meeting:'}</p>
                 <ol className="list-decimal ml-8 space-y-2">
                    <li>{language === 'marathi' ? 'मागील सभा वृत्तांत वाचून कायम करणे.' : 'Reading and confirming the minutes of the previous meeting.'}</li>
                    <li>{language === 'marathi' ? 'मुख्यमंत्री समृद्ध पंचायतराज अभियान प्रभावीपणे राबविणे.' : 'Effective implementation of Chief Minister Samruddha Panchayat Raj Abhiyan.'}</li>
                    <li className="text-justify">{language === 'marathi' ? 'आमचं गाव आमचा विकास (GPDP) सन २०२६-२७ चा ग्रामपंचायत विकास आराखडा अंतिम करणे बाबत.' : "Regarding finalization of Gram Panchayat Development Plan for 'Amcha Gaon Amcha Vikas' (GPDP) year 2026-27."}</li>
                    <li>{language === 'marathi' ? 'बालविवाह प्रतिबंध जनजागृती करणे.' : 'Creating public awareness on prevention of child marriage.'}</li>
                    <li>{language === 'marathi' ? 'ग्रामपंचायत करांची वसूली करणेबाबत.' : 'Regarding collection of Gram Panchayat taxes.'}</li>
                    <li className="flex flex-col">
                       <span className="dashed-line w-full h-4"></span>
                       <span className="dashed-line w-48 h-4 mt-1"></span>
                    </li>
                 </ol>
              </div>
           </div>

           <div className="absolute bottom-8 left-14 text-blue-700 text-sm underline print:hidden">
             www.majhigrampanchayat.com
           </div>
        </div>

        {/* PAGE 2 */}
        <div className="bg-white text-black shadow-lg print:shadow-none relative box-border flex flex-col page">
           <div className="text-[16px] space-y-6">
              <div className="flex">
                 <span className="mr-6">{language === 'marathi' ? '७)' : '7)'}</span>
                 <div className="flex flex-col w-full">
                    <span className="dashed-line w-full h-4"></span>
                    <span className="dashed-line w-48 h-4 mt-1"></span>
                 </div>
              </div>
              <div className="flex">
                 <span className="mr-6">{language === 'marathi' ? '८)' : '8)'}</span>
                 <div className="flex flex-col w-full">
                    <span className="dashed-line w-full h-4"></span>
                    <span className="dashed-line w-48 h-4 mt-1"></span>
                 </div>
              </div>

              <p className="mt-4">{language === 'marathi' ? 'आयत्यावेळी अध्यक्ष यांच्या परवानगीने येणारे विषय' : 'Topics arising at the eleventh hour with the permission of the Chairperson'}</p>
              <p className="flex justify-between items-end">
                 <span>{language === 'marathi' ? 'सदर सभेस आपण उपस्थित रहावे, ही विनंती.' : 'You are requested to attend the said meeting.'}</span>
                 <span>{language === 'marathi' ? 'आपला/आपली,' : 'Yours faithfully,'}</span>
              </p>

              <div className="flex justify-between items-start mt-12">
                 <div className="flex flex-col items-center space-y-4">
                    <p className="font-bold text-lg">{language === 'marathi' ? 'अधिकारी' : 'Officer'}</p>
                    <span className="dashed-line w-32 h-4"></span>
                 </div>
                 <div className="flex flex-col items-center space-y-4">
                    <p className="font-bold text-lg">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
                 </div>
                 <div className="flex flex-col items-center text-right">
                    <p className="font-bold text-lg">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</p>
                    <div className="flex items-end mt-2">
                       <span className="mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                       <span className="dashed-line w-24 h-4"></span>
                    </div>
                 </div>
              </div>

              <div className="pt-8">
                 <p className="mb-4">{language === 'marathi' ? 'सदरील ग्रामसभा दिनांक: २६/०१ / २०२६ ची सुचना आमच्या समक्ष नोटीस बोर्डावर डकविण्यात आली.' : 'The notice of the said Gramsabha dated 26/01/2026 was pasted on the notice board in our presence.'}</p>
                 <table className="w-full border-collapse border border-black">
                    <thead>
                       <tr>
                          <th className="border border-black p-2 w-16 text-center">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No.'}</th>
                          <th className="border border-black p-2 text-center">{language === 'marathi' ? 'ग्रामस्थांचे नाव' : 'Name of Villager'}</th>
                          <th className="border border-black p-2 w-48 text-center">{language === 'marathi' ? 'स्वाक्षरी' : 'Signature'}</th>
                       </tr>
                    </thead>
                    <tbody>
                       {[...Array(6)].map((_, i) => (
                          <tr key={i} style={{ height: '40px' }}>
                             <td className="border border-black p-2 text-center">{i + 1}</td>
                             <td className="border border-black p-2"></td>
                             <td className="border border-black p-2"></td>
                          </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
           </div>
        </div>

        {/* PAGE 3 */}
        <div className="bg-white text-black shadow-lg print:shadow-none relative box-border flex flex-col page">
           <table className="w-full border-collapse border border-black text-[16px] mb-8">
              <tbody>
                 {[...Array(5)].map((_, i) => (
                    <tr key={i + 7} style={{ height: '40px' }}>
                       <td className="border border-black p-2 w-16 text-center">{i + 7}</td>
                       <td className="border border-black p-2"></td>
                       <td className="border border-black p-2 w-48"></td>
                    </tr>
                 ))}
              </tbody>
           </table>

           <div className="text-center mb-6">
              <h1 className="text-2xl font-bold border-b-2 border-black inline-block pb-1">
                 {language === 'marathi' ? '26 जानेवारी ग्रामसभेसाठी नमुना दाखल विषय' : 'Sample subjects for 26th January Gramsabha'}
              </h1>
           </div>

           <div className="text-center mb-8">
              <p className="text-red-600 font-bold text-[16px] leading-relaxed">
                 {language === 'marathi' ? (
                   <>
                     (सूचना: ज्या जिल्ह्यात जिल्हा परिषद, पंचायत समितीच्या निवडणुकीची आचारसंहिता लागू आहे, <br/>
                     त्यांनी आचारसंहितेचे उल्लंघन होईल असे विषय ग्रामसभेत घेऊ नये)
                   </>
                 ) : (
                   <>
                     (Note: In districts where the code of conduct for Zilla Parishad and Panchayat Samiti elections is in force, <br/>
                     subjects violating the code of conduct should not be taken in the Gramsabha)
                   </>
                 )}
              </p>
           </div>

           <div className="text-[17px] space-y-4 ml-8">
              <div className="flex">
                 <span className="mr-4">1)</span>
                 <p className="text-justify">{language === 'marathi' ? 'वरिष्ठ कार्यालयाकडून आलेली परिपत्रके/ शासन निर्णय / व पत्र यांचे वाचन करून ग्रामसभेस माहिती देणे.' : 'Reading and informing the Gramsabha about circulars/GRs/letters received from higher offices.'}</p>
              </div>
              <div className="flex">
                 <span className="mr-4">2)</span>
                 <p>{language === 'marathi' ? 'मुख्यमंत्री समृद्ध पंचायतराज अभियान प्रभावीपणे राबविणे' : 'Effective implementation of Chief Minister Samruddha Panchayat Raj Abhiyan'}</p>
              </div>
              <div className="flex">
                 <span className="mr-4">3)</span>
                 <p>{language === 'marathi' ? 'करवसुली १००% करणेबाबत' : 'Regarding 100% tax recovery'}</p>
              </div>
              <div className="flex">
                 <span className="mr-4">4)</span>
                 <p className="text-justify">{language === 'marathi' ? 'महात्मा गांधी राष्ट्रीय ग्रामीण रोजगार हमी योजना सार्वजनिक कामे व वैयक्तिक लाभांची कामे लाभार्थी निवड करणे.' : 'Selection of beneficiaries for public works and individual benefit works under Mahatma Gandhi National Rural Employment Guarantee Scheme.'}</p>
              </div>
              <div className="flex">
                 <span className="mr-4">5)</span>
                 <p className="text-justify">{language === 'marathi' ? 'ज्या ग्रामपंचायतीची फेरआकारणीची मुदत संपलेली आहे, त्या ग्रामपंचायतीने समिती गठीत करून फेरआकारणी करणे.' : 'Gram Panchayats whose reassessment period has expired should constitute a committee and reassess.'}</p>
              </div>
           </div>

           <div className="absolute bottom-10 left-14 text-blue-700 text-sm underline print:hidden">
             www.majhigrampanchayat.com
           </div>
        </div>

        {/* PAGE 4 */}
        <div className="bg-white text-black shadow-lg print:shadow-none relative box-border flex flex-col page">
           <div className="text-[17px] space-y-4 ml-8 pt-4">
              <div className="flex">
                 <span className="mr-4">6)</span>
                 <p className="text-justify">{language === 'marathi' ? 'आमचं गाव आमचा विकास (GPDP) सन २०२६-२७ चा ग्रामपंचायत विकास आराखडा अंतिम करणे बाबत.' : "Regarding finalization of Gram Panchayat Development Plan for 'Amcha Gaon Amcha Vikas' (GPDP) year 2026-27."}</p>
              </div>
              <div className="flex">
                 <span className="mr-4">7)</span>
                 <p>{language === 'marathi' ? 'टी. बी. मुक्त, बालविवाह मुक्त पंचायत मोहिमेची जनजागृती करणेबाबत.' : 'Regarding public awareness of TB-free, Child-marriage-free Panchayat campaign.'}</p>
              </div>
              <div className="flex">
                 <span className="mr-4">8)</span>
                 <p>{language === 'marathi' ? 'ग्राम बाल संरक्षण समिती ग्राम स्तरावर गठीत करणे.' : 'Formation of Village Child Protection Committee at village level.'}</p>
              </div>
              <div className="flex">
                 <span className="mr-4">9)</span>
                 <p className="text-justify">{language === 'marathi' ? 'नवीन निवडणूक झालेल्या ग्रामपंचायतीमध्ये महाराष्ट्र ग्रामपंचायत अधिनियम १९५९ नुसार कलम ४९ नुसार उपसमित्या स्थापन करणे' : 'Establishing sub-committees under Section 49 of the Maharashtra Gram Panchayat Act 1959 in newly elected Gram Panchayats'}</p>
              </div>
              <div className="flex">
                 <span className="mr-4">10)</span>
                 <p className="text-justify ml-8">{language === 'marathi' ? 'घनकचरा व सांडपाणी व्यवस्थापनासाठी बंधित निधीमध्ये तरतुदी करणेकामी आराखड्यामध्ये आवश्यकतेनुसार बदल करणे प्रस्तावास मान्यता घेणे.' : 'Getting approval for the proposal to make necessary changes in the plan to make provisions in the tied fund for solid waste and wastewater management.'}</p>
              </div>
              <div className="flex">
                 <span className="mr-4">11)</span>
                 <p className="text-justify ml-8">{language === 'marathi' ? 'गाव ODF घोषित करणे. (Aspiring, rising, model घोषित करणेबाबत.)' : 'Declaring village ODF. (Regarding declaring Aspiring, rising, model.)'}</p>
              </div>
           </div>

           <div className="absolute bottom-10 left-14 text-blue-700 text-sm underline print:hidden">
             www.majhigrampanchayat.com
           </div>
        </div>
      </div>
    </div>
  );
};

export default GramsabhaNoticeNoAacharsanhitaWordForm;
